// frontend/src/hooks/useNotificationPreferences.js
/**
 * Notification Preferences Hook
 * 
 * React Query-based hook for fetching and updating notification preferences.
 * Features:
 * - Fetches user preferences with system defaults fallback
 * - Optimistic updates for better UX
 * - Toast notifications for success/error
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffAPI } from '../services/api';
import toast from '../utils/toast';

// Query keys
const PREFERENCE_KEYS = {
  all: ['notification-preferences'],
  current: ['notification-preferences', 'current'],
};

/**
 * Main notification preferences hook
 * @returns {Object} Preferences state and actions
 */
export const useNotificationPreferences = () => {
  const queryClient = useQueryClient();

  // Fetch preferences
  const {
    data: preferencesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: PREFERENCE_KEYS.current,
    queryFn: async () => {
      const response = await staffAPI.getNotificationPreferences();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (preferences don't change frequently)
  });

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: async (preferences) => {
      const response = await staffAPI.updateNotificationPreferences(preferences);
      return response.data;
    },
    onMutate: async (newPreferences) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: PREFERENCE_KEYS.all });

      // Snapshot previous value
      const previousPreferences = queryClient.getQueryData(PREFERENCE_KEYS.current);

      // Optimistically update
      queryClient.setQueryData(PREFERENCE_KEYS.current, (old) => {
        if (!old) return old;
        return {
          ...old,
          preferences: {
            ...old.preferences,
            ...newPreferences,
          },
        };
      });

      return { previousPreferences };
    },
    onError: (err, _, context) => {
      // Rollback on error
      queryClient.setQueryData(PREFERENCE_KEYS.current, context.previousPreferences);
      toast.error(err.response?.data?.preferences?.[0] || 'Failed to update preferences');
    },
    onSuccess: () => {
      toast.success('Notification preferences updated');
      queryClient.invalidateQueries({ queryKey: PREFERENCE_KEYS.all });
    },
  });

  // Birthday opt-out mutation
  const birthdayOptOutMutation = useMutation({
    mutationFn: async (optOut) => {
      const response = await staffAPI.updateBirthdayOptOut(optOut);
      return response.data;
    },
    onMutate: async (optOut) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCE_KEYS.all });
      const previous = queryClient.getQueryData(PREFERENCE_KEYS.current);
      queryClient.setQueryData(PREFERENCE_KEYS.current, (old) => {
        if (!old) return old;
        return { ...old, birthday_wish_opt_out: optOut };
      });
      return { previous };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(PREFERENCE_KEYS.current, context.previous);
      toast.error('Failed to update birthday preference');
    },
    onSuccess: (data) => {
      const opted = data.birthday_wish_opt_out;
      toast.success(opted ? 'You have opted out of birthday wishes' : 'You have opted in to birthday wishes');
      queryClient.invalidateQueries({ queryKey: PREFERENCE_KEYS.all });
    },
  });

  return {
    // Data
    preferences: preferencesData?.preferences || {},
    disabledByPolicy: preferencesData?.disabled_by_policy || [],
    lockedByAdmin: preferencesData?.locked_by_admin || {},
    birthdayWishOptOut: preferencesData?.birthday_wish_opt_out || false,
    loading: isLoading,
    error: error?.message || null,

    // Actions
    updatePreferences: (preferences) => updatePreferencesMutation.mutate(preferences),
    toggleBirthdayOptOut: (optOut) => birthdayOptOutMutation.mutate(optOut),
    refetchPreferences: refetch,

    // Mutation states
    isUpdating: updatePreferencesMutation.isPending,
    isBirthdayUpdating: birthdayOptOutMutation.isPending,
  };
};

export default useNotificationPreferences;

